import PropTypes from 'prop-types'
import GenericAction from '@/components/actions/GenericAction.tsx'
import { SquareTerminal } from 'lucide-react'
import { actionShape } from '@/components/propTypes.js'
import AActionContentGenericLayout from '@/components/actions/components/AActionContentGenericLayout.jsx'
import AActionContentTerminal from '@/components/actions/components/AActionContentTerminal.jsx'
import { getOutputVersion } from '@/lib/action-utils'

export default function TerminalSendAction({
  action,
  isLast = false,
  isInteractive = false,
}: {
  action: TerminalSendAction
  isLast: boolean
  isInteractive: boolean
}) {
  const terminalOutputs = []
  let detailsContent = `## ACTION:\n${action?.args?.comment}\n\n## COMMAND:\n${action?.args?.text || action?.args?.comment}\n\n`

  const outputs = action?.outputs
  let didError = false

  const output = formaTerminalOutput(action.outputs)

  if (Array.isArray(outputs)) {
    detailsContent += '## OUTPUTS:\n'
    outputs?.reduceRight((_, output) => {
      if (output == null) {
        return
      }
      let outputContent = output
      if (getOutputVersion(output) === 2) {
        outputContent = output?.content
        terminalOutputs.push(`${output?.message} \n\n`)
      }
      if (typeof outputContent === 'object' && outputContent !== null) {
        const outputEntries = Object.entries(outputContent)
          .filter(([, v]) => v != null && v !== '')
          .map(([key, value]) => `${key}: ${value}`)
        outputEntries.forEach(entry => {
          detailsContent += `- ${entry}\n`
          terminalOutputs.push(`- ${entry}\n`)
        })
        if (!didError) {
          didError = output?.error !== null
        }
      } else {
        terminalOutputs.push(`\n${output?.content} \n`)
      }
    }, null)
  }

  return (
    <GenericAction
      action={action}
      detailsContent={detailsContent}
      terminalOutputs={terminalOutputs}
      actionIcon={SquareTerminal}
      isLast={isLast}
      isInteractive={isInteractive}
      title={action?.args?.comment ?? 'Run command'}
    >
      <AActionContentGenericLayout>
        <AActionContentTerminal
          content={'$ ' + action?.args?.text}
          terminalOutputs={output}
          errored={didError}
        />
      </AActionContentGenericLayout>
    </GenericAction>
  )
}

function formaTerminalOutput(output: string | TerminalSendAction['outputs']) {
  if (typeof output === 'string') {
    return output
  }
  return output
    .map(output => {
      if (output.error) {
        return output.error
      }
      return output.content.terminal_contents_after_send
    })
    .join('\n')
}

TerminalSendAction.propTypes = {
  action: actionShape,
  isLast: PropTypes.bool,
  isInteractive: PropTypes.bool,
}

type TerminalSendAction = {
  args: {
    comment: string
    press_enter: boolean
    terminal_id: string
    text: string
  }
  subtype: null
  actionId: string
  elementId: string
  startSnapshotHash: null
  version: number
  name: string
  obsolete: boolean
  stepId: string
  iterationId: string
  hideArgsFromPrompt: boolean
  id: string
  endSnapshotHash: null
  type: string

  outputs: {
    content: {
      terminal_contents_after_send: string
    }
    status: string
    message: string
    error: string | null
  }[]
}

const sampleAction: TerminalSendAction = {
  args: {
    comment: 'Automatically add all shadcn/ui components',
    press_enter: true,
    terminal_id: 'session_1',
    text: "yes '' | npx shadcn@latest add -a",
  },
  subtype: null,
  actionId: 'action---HdJ1GSr9PJ89UJzESz5m---1---1---4',
  elementId: 'element---HdJ1GSr9PJ89UJzESz5m---1',
  startSnapshotHash: null,
  version: 2,
  name: 'terminal_send',
  obsolete: false,
  stepId: 'step---HdJ1GSr9PJ89UJzESz5m---1---1',
  iterationId: 'HdJ1GSr9PJ89UJzESz5m',
  hideArgsFromPrompt: false,
  id: 'action---HdJ1GSr9PJ89UJzESz5m---1---1---4',
  endSnapshotHash: null,
  type: 'function',
  startCommitHash: '77bea5ab9cd055e58a6c9efe1f511caafc3812a1',
  endCommitHash: '8ba396453114284a39f6cba157ed86556a77f3bb',
  outputs: [
    {
      content: {
        terminal_contents_after_send:
          "yes '' | npx shad- Preflight checks.                                                                                                                                                                    \n✔ Preflight checks.                                                                                                                                                                                     \n- Verifying framework.                                                                                                                                                                                  \nc✔ Verifying framework. Found Next.js.                                                                                                                                                                  \n- Validating Tailwind CSS config. Found v4.                                                                                                                                                             \n✔ Validating Tailwind CSS config. Found v4.                                                                                                                                                             \n- Validating import alias.                                                                                                                                                                              \n✔ Which color would you like to use as the base color? › Neutral                                                                                                                                        \n- Writing components.json.                                                                                                                                                                              \n✔ Writing components.json.                                                                                                                                                                              \n- Checking registry.                                                                                                                                                                                    \nlatest✔ Checking registry.                                                                                                                                                                              \n- Updating app/globals.css                                                                                                                                                                              \n✔ Updating app/globals.css                                                                                                                                                                              \n- Installing dependencies.                                                                                                                                                                              \n  Installing dependencies.                                                                                                                                                                              \n                                                                                                                                                                                                        \nIt looks like you are using React 19.                                                                                                                                                                   \nSome packages may fail to install due to peer dependency issues in npm (see https://ui.shadcn.com/react-19).                                                                                            \n                                                                                                                                                                                                        \n✔ How would you like to proceed? › Use --force                                                                                                                                                          \n- Installing dependencies.                                                                                                                                                                              \n add -a                                                                                                                                                                                                 \n                                                                                                                                                                                                        \n✔ Installing dependencies.                                                                                                                                                                              \n- Updating files.                                                                                                                                                                                       \n✔ Created 1 file:                                                                                                                                                                                       \n  - lib/utils.ts                                                                                                                                                                                        \n                                                                                                                                                                                                        \nSuccess! Project initialization completed.                                                                                                                                                              \nYou may now add components.                                                                                                                                                                             \n                                                                                                                                                                                                        \nyes: standard output: Broken pipe                                                                                                                                                                       \nbash /code/my-new-project#                                                                                                                                                                              \nbash /code/my-new-project# yes '' | npx shadcn@latest add -a",
      },
      status: 'success',
      message: "Successfully sent text to terminal sessions 'session_1'",
      error: null,
    },
  ],
  elapsedTime: 11.545182228088379,
  elementIndex: 1,
  stepIndex: 1,
}
